import { useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAccess, useCompleteRows } from './api/data';
import { command, name, type Row } from './api/client';
import { hrImportTemplateColumns, type HrImportPreviewResponse } from '@snaphire/contracts';
import { ActionForm, type Action } from './forms';
import {
  Button,
  Empty,
  ErrorState,
  Field,
  FilePicker,
  Loading,
  Select,
  Table,
  useToast,
} from './ui';

export const employeeImportTemplate =
  '\uFEFF' +
  hrImportTemplateColumns
    .map((column) => (column === 'Порядковый номер сотрудника' ? 'N' : column))
    .join(';') +
  '\r\n';
function downloadTemplate() {
  const url = URL.createObjectURL(
    new Blob([employeeImportTemplate], { type: 'text/csv;charset=utf-8' }),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'SnapHire-сотрудники.csv';
  anchor.click();
  URL.revokeObjectURL(url);
}

export function EmployeeBulkPage() {
  const access = useAccess();
  const navigate = useNavigate();
  const location = useRouterState({ select: (state) => state.location });
  const client = useQueryClient();
  const toast = useToast();
  const [employees] = useState(() => {
    try {
      const values: unknown = JSON.parse(
        String((location.search as { employees?: string }).employees || '[]'),
      );
      return Array.isArray(values)
        ? values
            .filter((value): value is { id: string; version: number } =>
              Boolean(
                value &&
                typeof value === 'object' &&
                typeof value.id === 'string' &&
                Number.isInteger(value.version) &&
                value.version >= 1,
              ),
            )
            .map(({ id, version }) => ({ id, version }))
        : [];
    } catch {
      return [];
    }
  });
  const back = () => void navigate({ to: '/people/employees' });
  if (access.isLoading) return <Loading />;
  if (!access.can('employees.edit')) return <Empty title="Недостаточно прав" />;
  if (employees.length > 250)
    return (
      <Empty
        title="Выбрано слишком много сотрудников"
        subtitle="За один раз можно изменить до 250 профилей. Уменьшите выбор в списке."
      >
        <Button onClick={back}>К сотрудникам</Button>
      </Empty>
    );
  if (!employees.length)
    return (
      <Empty title="Выберите сотрудников в списке">
        <Button onClick={back}>К сотрудникам</Button>
      </Empty>
    );
  const action: Action = {
    label: 'Изменить выбранных сотрудников',
    path: '/api/v1/employees/bulk' as Action['path'],
    permission: 'employees.edit',
    initial: { employees, patch: {}, reason: '' },
    hiddenFields: ['employees'],
    description: `Выбрано сотрудников: ${employees.length}. Заполните только поля, которые нужно изменить. Изменения сохранятся одновременно для всех сотрудников; при ошибке ни один профиль не изменится.`,
  };
  return (
    <ActionForm
      action={action}
      submitLabel="Применить изменения"
      onClose={back}
      onSuccess={() => {
        toast('Профили сотрудников обновлены');
        void client.invalidateQueries();
        back();
      }}
    />
  );
}

export function EmployeeImportPage() {
  const access = useAccess();
  const organizations = useCompleteRows('/api/v1/organizations', access.can('employees.import'));
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const [chosenOrganization, setOrganization] = useState('');
  const available = organizations.rows.filter((row) => row.status === 'ACTIVE');
  const organizationId = chosenOrganization || (available.length === 1 ? available[0]!.id : '');
  const [csv, setCsv] = useState('');
  const [filename, setFilename] = useState('');
  const [preview, setPreview] = useState<HrImportPreviewResponse | null>(null);
  const [pending, setPending] = useState<'preview' | 'import' | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [completed, setCompleted] = useState<number | null>(null);
  const canImport = access.can('employees.import') && access.can('employees.create');
  const run = async (mode: 'preview' | 'import') => {
    setPending(mode);
    setError(null);
    try {
      const result = await command(
        'POST',
        (mode === 'preview'
          ? '/api/v1/employees/import/preview'
          : '/api/v1/employees/import') as Action['path'],
        { organizationId, csv },
      );
      if (mode === 'preview') setPreview(result as unknown as HrImportPreviewResponse);
      else {
        setCompleted(
          Number(
            result.count ||
              (Array.isArray(result.items) ? result.items.length : preview?.validCount) ||
              0,
          ),
        );
        toast('Сотрудники импортированы');
        void client.invalidateQueries();
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error('Не удалось обработать файл'));
    } finally {
      setPending(null);
    }
  };
  if (access.isLoading) return <Loading />;
  if (!canImport)
    return (
      <Empty
        title="Недостаточно прав"
        subtitle="Для импорта нужны разрешения на импорт и создание сотрудников."
      />
    );
  if (completed !== null)
    return (
      <section className="form-page">
        <h1>Импорт завершён</h1>
        <div className="panel employee-import-result">
          <h2>Добавлено сотрудников: {completed}</h2>
          <Button className="primary" onClick={() => void navigate({ to: '/people/employees' })}>
            К сотрудникам
          </Button>
        </div>
      </section>
    );
  const rows: Row[] = (preview?.rows || []).map((row) => ({
    ...row,
    id: String(row.rowNumber),
    employeeNumber: row.values['Порядковый номер сотрудника'],
    employeeName: row.values['Имя'],
    email: row.values['Рабочая почта'],
  }));
  return (
    <section className="form-page employee-import-page">
      <div className="form-page-heading">
        <h1>Импорт сотрудников</h1>
      </div>
      <form
        className="domain-form"
        onSubmit={(event) => {
          event.preventDefault();
          void run('preview');
        }}
      >
        <div className="employee-import-intro">
          <p className="muted">
            Загрузите CSV до 250 сотрудников. Порядковый номер обязателен. Если рабочей почты нет,
            укажите прочерк «-». Номер сохраняется у сотрудника как постоянный идентификатор,
            включая ведущие нули; повторный номер блокирует импорт. Имя укажите в одном поле. Отдел,
            проект, должность, уровень, руководитель и локация указываются названием или кодом. В
            поле «Проект» можно перечислить несколько проектов через точку с запятой; первый станет
            основным. Проверка покажет ошибки и дубликаты до добавления сотрудников.
          </p>
          <Button icon="ArrowDown" onClick={downloadTemplate}>
            Скачать шаблон CSV
          </Button>
        </div>
        {organizations.isLoading ? (
          <Loading />
        ) : organizations.error ? (
          <ErrorState error={organizations.error} />
        ) : available.length !== 1 ? (
          <Field label="Организация">
            <Select
              aria-label="Организация"
              value={organizationId}
              required
              onChange={(event) => {
                setOrganization(event.target.value);
                setPreview(null);
              }}
            >
              <option value="">Выберите организацию</option>
              {available.map((row) => (
                <option value={row.id} key={row.id}>
                  {name(row)}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <FilePicker
          label="Файл сотрудников"
          description="CSV · не больше 5 МБ"
          name="employeeCsv"
          accept=".csv,text/csv"
          disabled={Boolean(pending)}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            setPreview(null);
            setCsv('');
            setError(null);
            setFilename(file?.name || '');
            if (!file) return;
            if (file.size > 5_000_000) {
              setError(new Error('Файл должен быть не больше 5 МБ'));
              return;
            }
            try {
              setCsv(await file.text());
            } catch {
              setError(new Error('Не удалось прочитать файл'));
            }
          }}
        />
        {filename && <p className="muted">Выбран файл: {filename}</p>}
        {error && <ErrorState error={error} />}
        <footer>
          <Button
            disabled={Boolean(pending)}
            onClick={() => void navigate({ to: '/people/employees' })}
          >
            Отмена
          </Button>
          <Button
            type="submit"
            className="primary"
            disabled={!csv || !organizationId || Boolean(pending)}
          >
            {pending === 'preview' ? 'Проверка…' : 'Проверить файл'}
          </Button>
        </footer>
      </form>
      {preview && (
        <section className="panel employee-import-preview">
          <header>
            <h2>Результат проверки</h2>
            <div className="employee-import-summary">
              <span>
                Готово к импорту: <strong>{preview.validCount}</strong>
              </span>
              <span>
                С ошибками: <strong>{preview.errorCount}</strong>
              </span>
              <span>
                Дубликаты: <strong>{preview.duplicateCount}</strong>
              </span>
            </div>
          </header>
          <Table
            rows={rows}
            columns={[
              { key: 'employeeNumber', label: 'Порядковый номер', width: 160 },
              { key: 'rowNumber', label: 'Строка', width: 100 },
              { key: 'employeeName', label: 'Сотрудник', width: 240 },
              { key: 'email', label: 'Рабочая почта', width: 280 },
              {
                key: 'result',
                label: 'Результат',
                width: 360,
                sortable: false,
                filter: false,
                render: (row) => {
                  const errors = Array.isArray(row.errors) ? row.errors : [];
                  return errors.length ? (
                    <span className="employee-import-errors">{errors.map(String).join('; ')}</span>
                  ) : row.duplicate ? (
                    <span className="employee-import-errors">Дубликат сотрудника</span>
                  ) : (
                    <span>Готово к импорту</span>
                  );
                },
              },
            ]}
          />
          <footer>
            <p className="muted">
              {preview.errorCount || preview.duplicateCount
                ? 'Исправьте файл и загрузите его повторно. При наличии ошибок сотрудники не добавляются.'
                : 'Проверка пройдена. При импорте файл будет проверен повторно, чтобы исключить дубликаты.'}
            </p>
            <Button
              className="primary"
              disabled={
                !preview.validCount ||
                Boolean(preview.errorCount || preview.duplicateCount || pending)
              }
              onClick={() => void run('import')}
            >
              {pending === 'import' ? 'Импорт…' : 'Импортировать сотрудников'}
            </Button>
          </footer>
        </section>
      )}
    </section>
  );
}
